import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { useGetMembershipContent } from '../services';
import { Loading, Error } from '../shared/components';
import MembershipForm from './MembershipForm';

const MembershipPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Use custom hooks
  const {
    contentData,
    isLoading,
    error: contentError,
  } = useGetMembershipContent();

  const handleFormSuccess = () => {
    setIsSubmitted(true);
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
    }, 3000);
  };

  // Loading state
  if (isLoading) {
    return <Loading />;
  }

  // Error state
  if (contentError && !contentData) {
    return <Error message={contentError} />;
  }

  if (isSubmitted) {
    return (
      <div className="py-6 sm:py-12">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8 text-center">
            <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-primary-500" />
            <h2 className="text-lg sm:text-2xl font-bold mb-4 text-primary-500">
              🎉 Thank you! Your registration has been received.
            </h2>
            <p className="text-base sm:text-lg text-secondary-700">
              We'll send you an email confirmation with upcoming event details
              shortly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 sm:py-12">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center mb-3 sm:mb-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-primary-500">
                {
                  contentData?.become_member_title_first?.[0]
                    ?.become_member_title_first_title
                }
              </h1>
            </div>
            <div className="w-12 sm:w-16 h-0.5 rounded bg-primary-500"></div>
          </div>

          {/* Welcome Section */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-2xl font-semibold mb-4 text-primary-500">
              {
                contentData?.become_member_title_second?.[0]
                  ?.become_member_title_second_title
              }
            </h2>
            <p className="text-base sm:text-lg leading-relaxed mb-4 sm:mb-6 text-secondary-700 whitespace-pre-line">
              {
                contentData?.become_member_title_second?.[0]
                  ?.become_member_title_second_description
              }
            </p>
          </div>

          {/* Club Membership Section */}
          <div className="mb-6 sm:mb-8">
            <h3 className="text-base sm:text-xl font-semibold mb-4 text-primary-500">
              {
                contentData?.become_member_title_third?.[0]
                  ?.become_member_title_third_title
              }
            </h3>
            <p className="text-sm sm:text-lg mb-4 sm:mb-6 text-secondary-700">
              📢{' '}
              {
                contentData?.become_member_title_third?.[0]
                  ?.become_member_title_third_description
              }
            </p>

            {/* Registration Form */}
            <MembershipForm onSuccess={handleFormSuccess} />
          </div>

          {/* Notes and Guidelines */}
          <div className="mb-6 sm:mb-8 p-3 sm:p-4 rounded-lg bg-gray-100">
            <h4 className="text-base sm:text-lg font-semibold mb-2 text-primary-500">
              📝{' '}
              {
                contentData?.become_member_title_four?.[0]
                  ?.become_member_title_four_title
              }
            </h4>
            <p className="text-sm sm:text-base text-secondary-700">
              {
                contentData?.become_member_title_four?.[0]
                  ?.become_member_title_four_description
              }
            </p>
          </div>

          {/* Need Help Section */}
          <div className="p-3 sm:p-4 rounded-lg bg-gray-100">
            <h4 className="text-base sm:text-lg font-semibold mb-2 text-primary-500">
              🤝{' '}
              {
                contentData?.become_member_title_five?.[0]
                  ?.become_member_title_five_title
              }
            </h4>
            <p className="text-sm sm:text-base mb-2 text-secondary-700">
              {
                contentData?.become_member_title_five?.[0]
                  ?.become_member_title_five_description
              }
            </p>
            <div className="space-y-1">
              <p className="text-sm sm:text-base text-secondary-700">
                <span className="font-medium">Email: </span>
                <a
                  href="mailto:contact@vlsiclub.org"
                  className="text-primary-500 underline hover:opacity-80"
                >
                  contact@vlsiclub.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
